import {
  applyDocumentColorScheme,
  getStoredThemeMode,
  isDarkMode,
  navThemeFor,
  setStoredThemeMode,
  subscribeSystemThemeChange,
  type ThemeMode,
} from '@/utils/themeMode';
import { MoonOutlined, SunOutlined } from '@ant-design/icons';
import { useIntl, useModel } from '@umijs/max';
import { Button, Tooltip } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

export const ThemeSwitch: React.FC = () => {
  const intl = useIntl();
  const { setInitialState } = useModel('@@initialState');
  const [mode, setMode] = useState<ThemeMode>(() => getStoredThemeMode());

  const applyMode = useCallback(
    (nextMode: ThemeMode) => {
      applyDocumentColorScheme(nextMode);
      setInitialState((preInitialState) => ({
        ...preInitialState,
        settings: {
          ...preInitialState?.settings,
          navTheme: navThemeFor(nextMode),
        },
      }));
    },
    [setInitialState],
  );

  // В режиме «схема ОС» следим за системными изменениями на лету
  useEffect(() => {
    if (mode !== 'system') return undefined;
    return subscribeSystemThemeChange(() => applyMode('system'));
  }, [mode, applyMode]);

  const dark = isDarkMode(mode);

  const toggle = () => {
    const nextMode: ThemeMode = dark ? 'light' : 'dark';
    setMode(nextMode);
    setStoredThemeMode(nextMode);
    applyMode(nextMode);
  };

  return (
    <Tooltip
      title={intl.formatMessage({
        id: dark ? 'component.themeSwitch.toLight' : 'component.themeSwitch.toDark',
        defaultMessage: dark ? 'Light theme' : 'Dark theme',
      })}
    >
      <Button
        type="text"
        aria-label={dark ? 'light-theme' : 'dark-theme'}
        icon={dark ? <SunOutlined /> : <MoonOutlined />}
        onClick={toggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 28,
          padding: '4px 8px',
          border: 'none',
          boxShadow: 'none',
          borderRadius: 4,
        }}
      />
    </Tooltip>
  );
};

export default ThemeSwitch;
